import React, {useState} from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {useGetProductionComponents} from '../../../hooks/productions/useProductions';
import {GetProductionComponentsRequestParam} from '../../../api/types/production.type';
import {ReactComponent as CaretDown} from '../../../assets/images/caret-down.svg';
import ArrButton from '../../../components/common/arr-button/ArrButton';

const ProductionComponentsApiTest: React.FC = () => {
  const [securityGroupId, setSecurityGroupId] = useState<number>(1);
  const [productionId, setProductionId] = useState<number>(31);
  const [parentSetId, setParentSetId] = useState<number>(1);
  const [moduleType, setModuleType] = useState<string>('Production');
  const [exportProductionId, setExportProductionId] = useState<number>(0);
  const [enabled, setEnabled] = useState<boolean>(false);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const {
    data: productionComponentsData,
    isLoading,
    error,
    refetch,
  } = useGetProductionComponents(
    {
      SecurityGroupId: securityGroupId,
      moduletype: moduleType,
      parentsetid: parentSetId,
      productionId: productionId,
      exportProductionId: exportProductionId,
    } as GetProductionComponentsRequestParam,
    enabled
  );

  const handleTestApi = () => {
    setEnabled(true);
  };

  const handleReset = () => {
    setEnabled(false);
  };

  const handleCopyJson = async () => {
    if (productionComponentsData) {
      try {
        await navigator.clipboard.writeText(
          JSON.stringify(productionComponentsData, null, 2)
        );
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  return (
    <Box className="demo-page" padding={3}>
      <Typography
        variant="h4"
        className="typo-text headline2_size"
        gutterBottom
      >
        🧪 Production Components API Test
      </Typography>
      <Typography
        className="typo-text body3_size"
        sx={{color: '#666', marginBottom: 2}}
      >
        Complex nested response with all production component configurations
      </Typography>
      <Box
        sx={{
          backgroundColor: '#f5f5f5',
          padding: 2,
          borderRadius: 1,
          marginBottom: 3,
        }}
      >
        <Typography variant="h6" className="typo-text subhead2_size">
          Request Parameters
        </Typography>

        <Box sx={{display: 'flex', gap: 2, marginTop: 2, flexWrap: 'wrap'}}>
          <TextField
            label="Security Group ID"
            type="number"
            value={securityGroupId}
            onChange={(e) => setSecurityGroupId(Number(e.target.value))}
            size="small"
          />
          <TextField
            label="Module Type"
            type="text"
            value={moduleType}
            onChange={(e) => setModuleType(e.target.value)}
            size="small"
            placeholder="Production"
          />
          <TextField
            label="Parent Set ID"
            type="number"
            value={parentSetId}
            onChange={(e) => setParentSetId(Number(e.target.value))}
            size="small"
          />
          <TextField
            label="Production ID"
            type="number"
            value={productionId}
            onChange={(e) => setProductionId(Number(e.target.value))}
            size="small"
          />
          <TextField
            label="Export Production ID"
            type="number"
            value={exportProductionId}
            onChange={(e) => setExportProductionId(Number(e.target.value))}
            size="small"
          />
        </Box>

        <Box sx={{marginTop: 2, display: 'flex', gap: 2}}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleTestApi}
            data-testid="production-components-api-test-test-api-button">
            🚀 Test API
          </Button>
          <Button
            variant="outlined"
            onClick={handleReset}
            data-testid="production-components-api-test-reset-button">
            🔄 Reset
          </Button>
          {enabled && (
            <Button
              variant="outlined"
              onClick={() => refetch()}
              data-testid="production-components-api-test-refetch-button">
              ♻️ Refetch
            </Button>
          )}
        </Box>
      </Box>
      {/* Loading State */}
      {isLoading && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#e3f2fd',
            borderRadius: 1,
            border: '1px solid #2196f3',
          }}
        >
          <Typography className="typo-text body2_size">
            ⏳ Loading... Making API request...
          </Typography>
        </Box>
      )}
      {/* Error State */}
      {error && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#ffebee',
            borderRadius: 1,
            border: '1px solid #f44336',
          }}
        >
          <Typography className="typo-text subhead3_size" color="error">
            ❌ Error
          </Typography>
          <Typography className="typo-text body3_size" color="error">
            {error.message}
          </Typography>
        </Box>
      )}
      {/* Success State - Display Response */}
      {productionComponentsData && !isLoading && (
        <Box>
          <Typography
            variant="h6"
            className="typo-text subhead2_size"
            gutterBottom
          >
            ✅ API Response
          </Typography>

          {/* High-Level Summary */}
          <Box
            sx={{
              padding: 2,
              backgroundColor: '#e8f5e9',
              borderRadius: 1,
              marginBottom: 2,
              border: '1px solid #4caf50',
            }}
          >
            <Typography className="typo-text label2_size" gutterBottom>
              📊 Production Configuration Summary
            </Typography>
            <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap', marginTop: 1}}>
              <Chip
                label={`Status: ${productionComponentsData.productionStatusCode}`}
                color="primary"
                size="small"
                data-testid={`production-component-chip-${productionComponentsData.productionStatusCode}`}
              />
              <Chip
                label={`Edit PDF: ${productionComponentsData.canEditPDFInExportFlag ? 'Yes' : 'No'}`}
                color={
                  productionComponentsData.canEditPDFInExportFlag
                    ? 'success'
                    : 'default'
                }
                size="small"
                data-testid={`production-component-chip-${productionComponentsData.canEditPDFInExportFlag}`}
              />
              <Chip
                label={`Edit TIFF: ${productionComponentsData.canEditTIFFInExportFlag ? 'Yes' : 'No'}`}
                color={
                  productionComponentsData.canEditTIFFInExportFlag
                    ? 'success'
                    : 'default'
                }
                size="small"
              />
              <Chip
                label={`Text Editable: ${productionComponentsData.isTextEditable ? 'Yes' : 'No'}`}
                color={
                  productionComponentsData.isTextEditable ? 'info' : 'default'
                }
                size="small"
                data-testid={`production-component-chip-${productionComponentsData.pagTypeValue}`}
              />
            </Box>
          </Box>

          {/* Component Selection Status */}
          <Box sx={{marginBottom: 2}}>
            <Typography className="typo-text label2_size" gutterBottom>
              📦 Selected Components
            </Typography>
            <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
              {productionComponentsData.productionComponentModel
                .isDATSelected && (
                <Chip label="DAT" color="primary" size="small" data-testid={`production-component-chip-DAT`}
                />
              )}
              {productionComponentsData.productionComponentModel
                .isNativeSelected && (
                <Chip label="Native" color="primary" size="small" data-testid={`production-component-chip-Native`} />
              )}
              {productionComponentsData.productionComponentModel
                .isTIFFSelected && (
                <Chip label="TIFF" color="primary" size="small" data-testid={`production-component-chip-TIFF`} />
              )}
              {productionComponentsData.productionComponentModel
                .isTextSelected && (
                <Chip label="Text" color="primary" size="small" data-testid={`production-component-chip-Text`} />
              )}
              {productionComponentsData.productionComponentModel
                .isPDFSelected && (
                <Chip label="PDF" color="primary" size="small" data-testid={`production-component-chip-PDF`} />
              )}
              {productionComponentsData.productionComponentModel
                .isArchiveSelected && (
                <Chip label="Archive" color="primary" size="small" data-testid={`production-component-chip-Archive`} />
              )}
              {productionComponentsData.productionComponentModel
                .isTranslation && (
                <Chip label="Translation" color="primary" size="small" data-testid={`production-component-chip-Translation`} />
              )}
              {productionComponentsData.productionComponentModel
                .isMP3FileSelected && (
                <Chip label="MP3" color="primary" size="small" data-testid={`production-component-chip-MP3`} />
              )}
            </Box>
          </Box>

          {/* Expandable Sections */}
          <Box sx={{marginTop: 2}}>
            {/* Tag Tree */}
            <Accordion>
              <AccordionSummary expandIcon={<CaretDown />}>
                <Typography className="typo-text label2_size">
                  🏷️ Tag Tree ({productionComponentsData.tagTree?.length || 0}{' '}
                  items)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <pre
                  style={{
                    fontSize: '12px',
                    overflow: 'auto',
                    maxHeight: '300px',
                  }}
                >
                  {JSON.stringify(productionComponentsData.tagTree, null, 2)}
                </pre>
              </AccordionDetails>
            </Accordion>

            {/* File Group Types */}
            <Accordion>
              <AccordionSummary expandIcon={<CaretDown />}>
                <Typography className="typo-text label2_size">
                  📁 File Group Types (
                  {productionComponentsData.fileGroupTypeList?.length || 0}{' '}
                  types)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                  {productionComponentsData.fileGroupTypeList?.map(
                    (fileType, index) => (
                      <Chip
                        key={index}
                        label={`${fileType.fileTypeLabel} (ID: ${fileType.fileTypeID})`}
                        color={
                          fileType.isFileTypeSelected ? 'primary' : 'default'
                        }
                        size="small"
                        data-testid={`production-component-chip-${fileType.fileTypeID}`}
                      />
                    )
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* ANSI Types */}
            <Accordion>
              <AccordionSummary expandIcon={<CaretDown />}>
                <Typography className="typo-text label2_size">
                  🔤 ANSI Types (
                  {productionComponentsData.ansiTypeList?.length || 0} types)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                  {productionComponentsData.ansiTypeList?.map((ansi, index) => (
                    <Chip
                      key={index}
                      label={`${ansi.ansiTypeLabel} (${ansi.ansiTypeID})`}
                      size="small"
                      variant="outlined"
                      data-testid={`production-component-chip-${ansi.ansiTypeLabel}`}
                    />
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Metadata Lists */}
            <Accordion>
              <AccordionSummary expandIcon={<CaretDown />}>
                <Typography className="typo-text label2_size">
                  📊 Metadata & Fields
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography className="typo-text body3_size" gutterBottom>
                  • Common Metadata:{' '}
                  {productionComponentsData.commonMetaDataList?.length || 0}
                </Typography>
                <Typography className="typo-text body3_size" gutterBottom>
                  • Production Fields:{' '}
                  {productionComponentsData.commonProductionFieldList?.length ||
                    0}
                </Typography>
                <Typography className="typo-text body3_size">
                  • Calculated Fields:{' '}
                  {productionComponentsData.calculatedFieldList?.length || 0}
                </Typography>
              </AccordionDetails>
            </Accordion>

            {/* DAT Component */}
            <Accordion>
              <AccordionSummary expandIcon={<CaretDown />}>
                <Typography className="typo-text label2_size">
                  📄 DAT Component Configuration
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <pre
                  style={{
                    fontSize: '12px',
                    overflow: 'auto',
                    maxHeight: '300px',
                  }}
                >
                  {JSON.stringify(
                    productionComponentsData.productionComponentModel
                      .datComponentModelData,
                    null,
                    2
                  )}
                </pre>
              </AccordionDetails>
            </Accordion>

            {/* Native Component */}
            <Accordion>
              <AccordionSummary expandIcon={<CaretDown />}>
                <Typography className="typo-text label2_size">
                  📁 Native Component Configuration
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <pre
                  style={{
                    fontSize: '12px',
                    overflow: 'auto',
                    maxHeight: '300px',
                  }}
                >
                  {JSON.stringify(
                    productionComponentsData.productionComponentModel
                      .nativeComponentModelData,
                    null,
                    2
                  )}
                </pre>
              </AccordionDetails>
            </Accordion>

            {/* Text Component */}
            <Accordion>
              <AccordionSummary expandIcon={<CaretDown />}>
                <Typography className="typo-text label2_size">
                  📝 Text Component Configuration
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <pre
                  style={{
                    fontSize: '12px',
                    overflow: 'auto',
                    maxHeight: '300px',
                  }}
                >
                  {JSON.stringify(
                    productionComponentsData.productionComponentModel
                      .textComponentData,
                    null,
                    2
                  )}
                </pre>
              </AccordionDetails>
            </Accordion>

            {/* TIFF Component */}
            <Accordion>
              <AccordionSummary expandIcon={<CaretDown />}>
                <Typography className="typo-text label2_size">
                  🖼️ TIFF Component Configuration
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <pre
                  style={{
                    fontSize: '12px',
                    overflow: 'auto',
                    maxHeight: '300px',
                  }}
                >
                  {JSON.stringify(
                    productionComponentsData.productionComponentModel
                      .tiffComponentModelData,
                    null,
                    2
                  )}
                </pre>
              </AccordionDetails>
            </Accordion>

            {/* PDF Component */}
            <Accordion>
              <AccordionSummary expandIcon={<CaretDown />}>
                <Typography className="typo-text label2_size">
                  📕 PDF Component Configuration
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <pre
                  style={{
                    fontSize: '12px',
                    overflow: 'auto',
                    maxHeight: '300px',
                  }}
                >
                  {JSON.stringify(
                    productionComponentsData.productionComponentModel
                      .pdfComponentModelData,
                    null,
                    2
                  )}
                </pre>
              </AccordionDetails>
            </Accordion>

            {/* MP3 Component */}
            <Accordion>
              <AccordionSummary expandIcon={<CaretDown />}>
                <Typography className="typo-text label2_size">
                  🎵 MP3 Component Configuration
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <pre
                  style={{
                    fontSize: '12px',
                    overflow: 'auto',
                    maxHeight: '300px',
                  }}
                >
                  {JSON.stringify(
                    productionComponentsData.productionComponentModel
                      .mP3ComponentModelData,
                    null,
                    2
                  )}
                </pre>
              </AccordionDetails>
            </Accordion>

            {/* Translation Component */}
            <Accordion>
              <AccordionSummary expandIcon={<CaretDown />}>
                <Typography className="typo-text label2_size">
                  🌐 Translation Component Configuration
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <pre
                  style={{
                    fontSize: '12px',
                    overflow: 'auto',
                    maxHeight: '300px',
                  }}
                >
                  {JSON.stringify(
                    productionComponentsData.productionComponentModel
                      .translationComponentModelData,
                    null,
                    2
                  )}
                </pre>
              </AccordionDetails>
            </Accordion>

            {/* Archive Component */}
            <Accordion>
              <AccordionSummary expandIcon={<CaretDown />}>
                <Typography className="typo-text label2_size">
                  📦 Archive Component Configuration
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <pre
                  style={{
                    fontSize: '12px',
                    overflow: 'auto',
                    maxHeight: '300px',
                  }}
                >
                  {JSON.stringify(
                    productionComponentsData.productionComponentModel
                      .archiveFileComponentData,
                    null,
                    2
                  )}
                </pre>
              </AccordionDetails>
            </Accordion>

            {/* Full Response */}
            <Accordion>
              <AccordionSummary expandIcon={<CaretDown />}>
                <Typography className="typo-text label2_size">
                  📄 Full JSON Response
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{marginBottom: 2}}>
                  <ArrButton
                    variant={copySuccess ? 'secondary' : 'primary'}
                    size="small"
                    onClick={handleCopyJson}
                    dataTestId="production-components-api-test-button">
                    {copySuccess ? '✅ Copied!' : '📋 Copy JSON'}
                  </ArrButton>
                </Box>
                <Box
                  sx={{
                    padding: 2,
                    backgroundColor: '#263238',
                    borderRadius: 1,
                    overflow: 'auto',
                    maxHeight: '500px',
                  }}
                >
                  <pre
                    style={{
                      color: '#4caf50',
                      fontSize: '12px',
                      margin: 0,
                      fontFamily: 'monospace',
                    }}
                  >
                    {JSON.stringify(productionComponentsData, null, 2)}
                  </pre>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
      )}
      {/* Initial State */}
      {!isLoading && !productionComponentsData && !error && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#fff3e0',
            borderRadius: 1,
            border: '1px solid #ff9800',
          }}
        >
          <Typography className="typo-text body2_size">
            {!enabled
              ? '👆 Click "Test API" button to make the API request'
              : '⏳ Preparing request...'}
          </Typography>
        </Box>
      )}
      {/* Network Details */}
      <Box sx={{marginTop: 4, padding: 2, backgroundColor: '#fafafa'}}>
        <Typography variant="h6" className="typo-text subhead3_size">
          🌐 Network Details
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Endpoint:</strong>{' '}
          /productions/api/v1/production/view/productioncomponents
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Method:</strong> GET
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Query Params:</strong>
        </Typography>
        <pre
          style={{
            fontSize: '12px',
            backgroundColor: '#fff',
            padding: '8px',
            borderRadius: '4px',
          }}
        >
          {`SecurityGroupId=${securityGroupId}
moduletype=${moduleType}
parentsetid=${parentSetId}
productionId=${productionId}
exportProductionId=${exportProductionId}`}
        </pre>
        <Typography className="typo-text body3_size">
          <strong>Base URL:</strong>{' '}
          https://arr-api-productions-dev.us2kd2w30.k8d.consiliotest.com/
        </Typography>
      </Box>
    </Box>
  );
};

export default ProductionComponentsApiTest;
